import { useState } from 'react'
import { ChevronDown, ChevronUp, MessageSquare } from 'lucide-react'
import { cn } from '../atoms'
import { splitEmailQuotes } from '../../utils/emailQuoteSplitter'
import { formatEmailDescription } from '../../utils/emailParser'
import { sanitizeHtmlBody } from '../../utils/assetUrl'

export interface QuotedTextCollapsibleProps {
  content?: string | null
  text?: string | null
  htmlContent?: string | null
  className?: string
  cleanClassName?: string
  quotedClassName?: string
  defaultExpanded?: boolean
  onToggle?: (expanded: boolean) => void
}

/**
 * Renders email communication content with previous quoted thread chains
 * cleanly collapsed behind an interactive toggle.
 */
export function QuotedTextCollapsible({
  content,
  text,
  htmlContent,
  className,
  cleanClassName,
  quotedClassName,
  defaultExpanded = false,
  onToggle,
}: QuotedTextCollapsibleProps) {
  const raw = htmlContent || content || text || ''
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  const { cleanContent, quotedContent, hasQuotedText, isHtml } = splitEmailQuotes(raw)

  const handleToggle = () => {
    const next = !isExpanded
    setIsExpanded(next)
    onToggle?.(next)
  }

  if (!raw) {
    return <span className="text-gray-400 italic text-sm">Sin contenido</span>
  }

  const renderBody = (body: string, isQuoted = false) => {
    if (isHtml) {
      const sanitized = sanitizeHtmlBody(body)
      return (
        <div
          className={cn(
            'prose prose-sm max-w-none text-gray-800 dark:text-gray-200 leading-relaxed overflow-hidden break-words',
            isQuoted ? 'text-xs text-gray-600 dark:text-gray-400' : ''
          )}
          dangerouslySetInnerHTML={{ __html: sanitized }}
        />
      )
    }

    // Plain text rendering with blockquote formatting
    return (
      <div
        className={cn(
          'text-sm text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-wrap break-words',
          isQuoted ? 'text-xs text-gray-600 dark:text-gray-400 font-mono' : ''
        )}
      >
        {body}
      </div>
    )
  }

  return (
    <div className={cn('w-full', className)}>
      {/* Clean author content */}
      <div className={cn('break-words', cleanClassName)}>
        {renderBody(cleanContent)}
      </div>

      {/* Quoted thread collapsible section */}
      {hasQuotedText && quotedContent && (
        <div className="mt-2.5 pt-1.5">
          <button
            type="button"
            onClick={handleToggle}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-primary-400"
            title={isExpanded ? 'Ocultar texto citado' : 'Ver mensaje citado anterior'}
            aria-expanded={isExpanded}
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-3.5 h-3.5" />
                <span>Ocultar texto citado</span>
              </>
            ) : (
              <>
                <ChevronDown className="w-3.5 h-3.5" />
                <span className="text-gray-400 font-mono">···</span>
                <span>Ver mensaje citado anterior...</span>
              </>
            )}
          </button>

          {isExpanded && (
            <div
              className={cn(
                'mt-2 pl-3 py-2 border-l-2 border-primary-300 dark:border-primary-700 bg-gray-50/80 dark:bg-gray-800/40 rounded-r-lg text-xs transition-all animate-in fade-in duration-200 overflow-x-auto',
                quotedClassName
              )}
            >
              <div className="flex items-center gap-1 text-[11px] font-medium text-gray-400 mb-1.5">
                <MessageSquare className="w-3 h-3" />
                <span>Texto citado anterior</span>
              </div>
              {renderBody(quotedContent, true)}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
